import { useEffect, useRef } from 'react';
import useDragPreviewManagerValue from 'layout-dnd-utils/DragPreview/useDragPreviewManagerValue';
import { useDragPreviewManager } from 'layout-dnd-utils/DragPreview/DragPreviewManager';
import { areConstraintBoundsEqual, clampDragPreviewPosition } from 'layout-dnd-utils/DragPreview/clampDragPreviewPosition';
import { useTransitionStyles } from 'layout-dnd-utils/TransitionStyles/TransitionStylesContext';
import { dropTypes } from 'layout-dnd-utils/constants/dnd';
import { areGridBoundingBoxPositionsEqual } from 'layout-data-lib/GridLayoutDataTree/utils';
/**
 * When center-line coercion grows an element's columnsSpanned, its original
 * columnStart may place the span past the right edge of the pegboard, which
 * would cause CSS Grid to add implicit columns and stretch the pegboard.
 * This clamps the column start so the coerced span fits within the pegboard.
 */
const clampCoercedColumnStart = ({
  originalColumnStart,
  coercedColumnsSpanned,
  pegboardColumns
}) => {
  const maxStartColumn = pegboardColumns - coercedColumnsSpanned + 1;
  return Math.min(originalColumnStart, maxStartColumn);
};
function hasDragPreviewLayoutChanged(previousSnapshot, nextSnapshot) {
  if (!previousSnapshot) {
    return true;
  }
  return !areConstraintBoundsEqual(previousSnapshot.pegboardBounds, nextSnapshot.pegboardBounds) || !areGridBoundingBoxPositionsEqual(previousSnapshot.gridBoundingBox, nextSnapshot.gridBoundingBox);
}
const useScrollPosition = () => {
  const scrollPositionRef = useRef({
    initialLeft: window.scrollX,
    initialTop: window.scrollY,
    adjustedLeft: 0,
    adjustedTop: 0,
    currentLeft: window.scrollX,
    currentTop: window.scrollY
  });
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current.adjustedLeft = window.scrollX - scrollPositionRef.current.initialLeft;
      scrollPositionRef.current.adjustedTop = window.scrollY - scrollPositionRef.current.initialTop;
      scrollPositionRef.current.currentLeft = window.scrollX;
      scrollPositionRef.current.currentTop = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // eslint-disable-next-line react-hooks/refs
  return scrollPositionRef.current;
};
/**
 * Renders a shadowed drag preview of the element to be dragged, with the same
 * size and position as the dragged element. The dragged element is updated to
 * then follow the cursor position when the cursor is moved.
 */
const DragPreview = ({
  scale = 1,
  element,
  sourceClientOffset,
  differenceFromInitialOffset,
  pegboardBounds,
  currentTranslationCoords,
  dropTarget,
  gridBoundingBox,
  widthOverride,
  heightOverride,
  zIndex: zIndexOverride,
  remeasureOnPegboardChange = true
}) => {
  const dragPreviewManager = useDragPreviewManager();
  const transitionStylesManager = useTransitionStyles();
  const globalZIndex = useDragPreviewManagerValue(_dragPreviewManager => _dragPreviewManager.getDragPreviewZIndex());
  const zIndex = zIndexOverride !== undefined ? String(zIndexOverride) : globalZIndex;
  const {
    adjustedLeft,
    adjustedTop,
    currentLeft,
    currentTop
  } = useScrollPosition();
  const originalWidthRef = useRef(null);
  const dragPreviewDimensions = useRef(null);
  const dragPreviewPositionOffset = useRef({
    left: 0,
    top: 0
  });
  const previousLayoutSnapshot = useRef(null);
  useEffect(() => {
    return () => {
      dragPreviewManager.setDragPreviewPosition({
        elementWidth: 0,
        elementHeight: 0,
        elementLeft: 0,
        elementTop: 0
      });
    };
  }, [dragPreviewManager]);
  useEffect(() => {
    if (element) {
      transitionStylesManager.registerTransitionStyle(element, 'transform');
      transitionStylesManager.registerTransitionStyle(element, 'z-index');
      transitionStylesManager.registerTransitionStyle(element, 'grid-column');
    }
  }, [element, transitionStylesManager]);

  // Updates the dragged element to follow the cursor position when the cursor
  // is moved. It's adjusted to account for a cursor offset and scroll position.
  // eslint-disable-next-line react-hooks/immutability
  useEffect(() => {
    if (element && differenceFromInitialOffset && sourceClientOffset) {
      var _ref;
      let deltaX = 0;
      let deltaY = 0;
      if (!dragPreviewDimensions.current) {
        dragPreviewDimensions.current = element.getBoundingClientRect();
      }
      const {
        height: rectHeight,
        width: rectWidth
      } = dragPreviewDimensions.current;
      if ((dropTarget === null || dropTarget === void 0 ? void 0 : dropTarget.type) === dropTypes.CENTER_ON_PEGBOARD && pegboardBounds) {
        if (originalWidthRef.current == null) {
          originalWidthRef.current = rectWidth;
        }
        const previewStartColumn = clampCoercedColumnStart({
          originalColumnStart: gridBoundingBox.columnLeft,
          coercedColumnsSpanned: dropTarget.columnsSpanned,
          pegboardColumns: pegboardBounds.numColumns
        });
        // eslint-disable-next-line react-hooks/immutability
        element.style.gridColumn = `${previewStartColumn} / span ${dropTarget.columnsSpanned}`;
      } else if (originalWidthRef.current != null) {
        originalWidthRef.current = null;
        element.style.removeProperty('grid-column');
      }
      const effectiveWidth = (_ref = widthOverride !== null && widthOverride !== void 0 ? widthOverride : originalWidthRef.current) !== null && _ref !== void 0 ? _ref : rectWidth;
      const effectiveHeight = heightOverride !== null && heightOverride !== void 0 ? heightOverride : rectHeight;
      if (pegboardBounds) {
        ({
          deltaX,
          deltaY
        } = clampDragPreviewPosition({
          sourceX: sourceClientOffset.x,
          sourceY: sourceClientOffset.y,
          elementWidth: effectiveWidth,
          bounds: pegboardBounds
        }));
      }
      let panDeltaX = 0;
      let panDeltaY = 0;
      if (currentTranslationCoords) {
        const startCoords = dragPreviewManager.getDragStartTranslationCoords();
        panDeltaX = currentTranslationCoords.translateX - startCoords.translateX;
        panDeltaY = currentTranslationCoords.translateY - startCoords.translateY;
      }
      const x = (differenceFromInitialOffset.x + adjustedLeft + deltaX - panDeltaX) / scale;
      const y = (differenceFromInitialOffset.y + adjustedTop + deltaY - panDeltaY) / scale;
      element.style.transform = `translate(${x}px, ${y}px)`;
      element.style.zIndex = zIndex;
      const inferredPosition = {
        left: sourceClientOffset.x + deltaX + currentLeft,
        top: sourceClientOffset.y + deltaY + currentTop
      };

      // Multi-item drags mount one DragPreview per selected node, all
      // writing to the same DragPreviewManager singleton. A per-node
      // measured offset isn't a well-defined "position of the group", so
      // multi-item drags pass remeasureOnPegboardChange={false} to opt out
      // of measuring and use the raw inferred position instead.
      if (remeasureOnPegboardChange) {
        const nextLayoutSnapshot = {
          pegboardBounds,
          gridBoundingBox
        };

        // Avoid a forced layout on every pointer update. The preview's offset
        // from react-dnd's inferred position only changes when its grid layout
        // changes, so measure once at drag start and after a pegboard reflow.
        if (hasDragPreviewLayoutChanged(previousLayoutSnapshot.current, nextLayoutSnapshot)) {
          const renderedRect = element.getBoundingClientRect();
          dragPreviewPositionOffset.current = {
            left: renderedRect.left + window.scrollX - inferredPosition.left,
            top: renderedRect.top + window.scrollY - inferredPosition.top
          };
          previousLayoutSnapshot.current = nextLayoutSnapshot;
        }
      }
      const reportedPosition = {
        elementWidth: effectiveWidth,
        elementHeight: effectiveHeight,
        elementLeft: inferredPosition.left + (remeasureOnPegboardChange ? dragPreviewPositionOffset.current.left : 0),
        elementTop: inferredPosition.top + (remeasureOnPegboardChange ? dragPreviewPositionOffset.current.top : 0)
      };
      dragPreviewManager.setDragPreviewPosition(reportedPosition);
    }
  }, [element, adjustedLeft, adjustedTop, currentLeft, currentTop, zIndex, scale, differenceFromInitialOffset, sourceClientOffset, dragPreviewManager, pegboardBounds, currentTranslationCoords, dropTarget, gridBoundingBox, widthOverride, heightOverride, remeasureOnPegboardChange]);
  return null;
};
export default DragPreview;